/**
 * Static docs registry.
 *
 * The site ships a small, curated set of long-form articles. Adding a new
 * article is two steps:
 *
 * 1. Drop Chinese markdown in `src/content/docs/<slug>.md`.
 * 2. Drop English markdown in `src/content/docs/en/<slug>.md`.
 * 3. Append base metadata and localized title/summary below.
 *
 * The body is loaded lazily via `loadDocBody(slug)` so the homepage and
 * the docs index never pay the parse cost of articles the user is not
 * reading. Article slugs are validated against `ARTICLE_BASE` before render
 * to keep `<Outlet>`-style 404 handling honest.
 */
import { marked } from "marked";
import { coerceLanguage, type SupportedLanguage } from "../i18n/index";

export interface DocMeta {
  /** URL slug under `/docs/`. Must be unique and URL-safe. */
  slug: string;
  /** Display title shown in the index and the article header. */
  title: string;
  /** One-line summary shown in the index card. */
  summary: string;
  /** Approx read time in minutes. Hand-counted; rough is fine. */
  readMinutes: number;
  /** ISO date of the last meaningful update. */
  updated: string;
  /** Lower = earlier in the index. */
  order: number;
}

const ARTICLE_BASE: ReadonlyArray<Omit<DocMeta, "title" | "summary" | "readMinutes">> = [
  {
    slug: "what-is-tokenbuddy",
    updated: "2026-06-07",
    order: 1,
  },
];

const LOCALIZED_META: Record<SupportedLanguage, Readonly<Record<string, Pick<DocMeta, "title" | "summary" | "readMinutes">>>> = {
  en: {
    "what-is-tokenbuddy": {
      title: "What is TokenBuddy",
      summary:
        "The cost, availability, prepaid, routing, and model-quality problems TokenBuddy solves, plus its business participants and payment flow.",
      readMinutes: 5,
    },
  },
  "zh-Hans": {
    "what-is-tokenbuddy": {
      title: "What is TokenBuddy",
      summary:
        "TokenBuddy 解决的成本、可用性、预付、路由和模型质量问题，以及它的业务参与者和支付流。",
      readMinutes: 5,
    },
  },
};

export function listDocs(language: string = "en"): ReadonlyArray<DocMeta> {
  const lang = coerceLanguage(language);
  return ARTICLE_BASE.map((article) => ({
    ...article,
    ...LOCALIZED_META[lang][article.slug],
  })).sort((a, b) => a.order - b.order);
}

export function getDocMeta(slug: string, language: string = "en"): DocMeta | undefined {
  return listDocs(language).find((article) => article.slug === slug);
}

// Vite resolves `?raw` at build time. `eager: true` makes the lookup
// synchronous so the article render is one render pass instead of two.
const RAW: Readonly<Record<string, string>> = import.meta.glob(
  "../content/docs/**/*.md",
  { query: "?raw", import: "default", eager: true },
);

function fileForSlug(slug: string, language: string): string {
  const lang = coerceLanguage(language);
  return lang === "zh-Hans"
    ? `../content/docs/${slug}.md`
    : `../content/docs/${lang}/${slug}.md`;
}

/**
 * Render the markdown body for a slug as sanitized HTML.
 *
 * `marked` is configured with a minimal allow-list and `gfm: true` so
 * tables and strikethrough work in the public-facing content. We do
 * not allow raw HTML in user-supplied content; today all article
 * bodies ship with the repo so this is a defence-in-depth check, not
 * the only line of defence.
 *
 * If the markdown starts with an `<h1>`, it is stripped — the article
 * page renders the title itself (from `DocMeta.title`), so leaving the
 * H1 in would double-render it.
 */
export function loadDocBody(slug: string, language: string = "en"): string {
  const body = RAW[fileForSlug(slug, language)];
  if (body === undefined) {
    throw new Error(
      `No markdown file for slug "${slug}" and language "${language}". Add the localized markdown under src/content/docs/.`,
    );
  }
  marked.setOptions({ gfm: true, breaks: false });
  const html = marked.parse(body, { async: false }) as string;
  return html.replace(/^<h1[^>]*>[\s\S]*?<\/h1>\s*/, "");
}
